/**
 * « Veux-tu t'évaluer ? » (`/evaluer`, migration 0328) : bilan de niveau d'un
 * adulte (Curieux ou parent). Il choisit le niveau qu'il pense avoir, répond à
 * 15 questions (arrêt / reprise quand il veut), puis lit son bilan rédigé par
 * IA — pour un parent, sur quoi il peut aider son enfant. Les questions, la fin
 * et le résultat réutilisent le diagnostic de programme (`DiagnosticPage`).
 */

import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Compass, RotateCw } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import {
  ADULT_LEVEL_CHOICES,
  adultDiagnosticStateKey,
  adultIntroCopy,
  adultLevelLabel,
  startAdultDiagnostic,
  useAdultDiagnosticState,
  type AdultLevel,
} from '@/lib/skill-diagnostic';
import { DiagnosticPage } from './diagnostic-page';

export function AdultDiagnosticPage() {
  const { user, profile } = useAuth();
  const qc = useQueryClient();
  const state = useAdultDiagnosticState(user?.id);
  const inProgress = state.data?.in_progress ?? null;
  const [level, setLevel] = useState<AdultLevel | null>(null);
  // Niveau proposé d'office : bilan en cours, sinon celui du dernier bilan.
  const chosen = (level ??
    (inProgress?.level as AdultLevel | undefined) ??
    (state.data?.last_completed?.level as AdultLevel | undefined) ??
    null) as AdultLevel | null;
  // Niveau réellement lancé (lu au moment du départ, même juste après un clic).
  const startLevel = useRef<AdultLevel | null>(null);
  const exitTo = profile?.role === 'parent' ? '/parent/dashboard' : '/player/dashboard';

  return (
    <DiagnosticPage
      adult={{
        exitTo,
        start: (restart) => startAdultDiagnostic(startLevel.current ?? chosen ?? '3e', restart),
        onRefresh: () =>
          void qc.invalidateQueries({ queryKey: adultDiagnosticStateKey(user?.id ?? '') }),
        renderIntro: ({ begin, showResult }) => (
          <AdultIntro
            role={state.data?.role ?? profile?.role ?? null}
            loading={state.isLoading}
            available={state.data?.available ?? true}
            level={chosen}
            onLevel={setLevel}
            inProgress={inProgress}
            lastCompleted={state.data?.last_completed ?? null}
            onStart={() => {
              startLevel.current = chosen;
              begin(!!inProgress && inProgress.level !== chosen);
            }}
            onResume={() => {
              const l = (inProgress?.level as AdultLevel) ?? null;
              startLevel.current = l;
              setLevel(l);
              begin(false);
            }}
            onShowResult={showResult}
          />
        ),
      }}
    />
  );
}

export function AdultIntro({
  role,
  loading,
  available,
  level,
  onLevel,
  inProgress,
  lastCompleted,
  onStart,
  onResume,
  onShowResult,
}: {
  role: string | null;
  loading: boolean;
  available: boolean;
  level: AdultLevel | null;
  onLevel: (l: AdultLevel) => void;
  inProgress: {
    id: string;
    level: string | null;
    answered_count: number;
    question_count: number;
  } | null;
  lastCompleted: { id: string; level: string | null; mastery_pct: number | null } | null;
  onStart: () => void;
  onResume: () => void;
  onShowResult: (id: string) => void;
}) {
  const copy = adultIntroCopy(role);
  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Compass className="text-primary-600 h-6 w-6" aria-hidden />
          <h1 className="font-display text-ink-900 text-2xl font-extrabold">{copy.title}</h1>
        </div>
        <p className="text-ink-600 text-sm">{copy.lead}</p>
      </div>

      {loading ? (
        <div className="bg-ink-100 h-24 animate-pulse rounded-2xl" />
      ) : !available ? (
        <p className="text-ink-500 text-sm">
          Les élèves ont leur propre diagnostic de programme, sur leur tableau de bord.
        </p>
      ) : (
        <>
          {inProgress && (
            <div className="border-primary-200 bg-primary-50/60 ocean:bg-white/5 space-y-2 rounded-2xl border p-4">
              <p className="text-ink-800 text-sm">
                Bilan en cours · niveau {adultLevelLabel(inProgress.level)} ·{' '}
                {inProgress.answered_count}/{inProgress.question_count} questions
              </p>
              <Button fullWidth onClick={onResume}>
                Reprendre ({inProgress.answered_count}/{inProgress.question_count})
              </Button>
              {inProgress.answered_count >= 5 && (
                <Button variant="ghost" fullWidth onClick={() => onShowResult(inProgress.id)}>
                  Voir mon bilan partiel
                </Button>
              )}
            </div>
          )}

          <fieldset className="space-y-2">
            <legend className="text-ink-900 text-sm font-semibold">{copy.levelQuestion}</legend>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {ADULT_LEVEL_CHOICES.map((l) => {
                const on = level === l.value;
                return (
                  <button
                    key={l.value}
                    type="button"
                    aria-pressed={on}
                    onClick={() => onLevel(l.value)}
                    className={cn(
                      'min-h-[2.75rem] rounded-xl border-2 px-3 text-sm font-semibold active:scale-[0.98]',
                      on
                        ? 'border-primary-500 bg-primary-50 text-primary-700 ocean:bg-primary-500/15 ocean:text-primary-200'
                        : 'border-ink-200 text-ink-700 ocean:bg-white/5 bg-white',
                    )}
                  >
                    {l.label}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <Button fullWidth size="lg" disabled={!level} onClick={onStart}>
            {inProgress && inProgress.level !== level ? (
              <>
                <RotateCw className="h-4 w-4" aria-hidden /> Nouveau bilan ·{' '}
                {adultLevelLabel(level)}
              </>
            ) : (
              `C’est parti · 15 questions${level ? ` (${adultLevelLabel(level)})` : ''}`
            )}
          </Button>

          {lastCompleted && (
            <Button variant="outline" fullWidth onClick={() => onShowResult(lastCompleted.id)}>
              Voir mon dernier bilan ({adultLevelLabel(lastCompleted.level)} ·{' '}
              {lastCompleted.mastery_pct ?? 0} %)
            </Button>
          )}
        </>
      )}
    </div>
  );
}
